// frontend/src/pages/Login.jsx
// Business owner login (login only — signup is at /register).
// After login: check email confirmed → check subscription → redirect.

import { useState, useEffect } from 'react';
import { Navigate, Link, useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { useAuth } from '../utils/AuthContext';
import { useLanguage } from '../utils/i18n';
import { validateField } from '../utils/validation';
import { useNotification } from '../utils/NotificationContext';
import LanguageToggle from '../components/LanguageToggle';
import Logo from '../components/Logo';
import { ArrowLeft } from 'lucide-react';

export default function Login() {
  const { session, loading: authLoading, subscriptionStatus, isEmailConfirmed } = useAuth();
  const { t } = useLanguage();
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Redirect already-logged-in users
  if (!authLoading && session && isEmailConfirmed && !loading) {
    if (subscriptionStatus === 'active' || subscriptionStatus === 'trialing') {
      return <Navigate to="/dashboard" replace />;
    } else {
      return <Navigate to="/pricing" replace />;
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!validateField('email', email, true)) {
      setError(t('err_valid_email'));
      setLoading(false);
      return;
    }
    if (password.length < 6) {
      setError(t('err_password_min'));
      setLoading(false);
      return;
    }

    try {
      const { data, error: authErr } = await supabase.auth.signInWithPassword({ email, password });
      if (authErr) throw authErr;

      // Check if email is confirmed
      const REQUIRE_CONFIRM = process.env.REACT_APP_REQUIRE_EMAIL_CONFIRMATION === 'true' || process.env.REACT_APP_REQUIRE_EMAIL_CONFIRMATION === undefined;
      
      if (REQUIRE_CONFIRM && !data.session?.user?.email_confirmed_at) {
        // Sign out immediately — they shouldn't be logged in
        await supabase.auth.signOut();
        setError(t('err_confirm_email_first'));
        setLoading(false);
        return;
      }

      showNotification(t('notify_welcome_back'), 'success');
      // AuthContext will pick up the session change and the redirect
      // logic above will handle it on re-render
    } catch (err) {
      console.error('[Auth Error]', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg)', position: 'relative' }}>
      {/* Responsive Header */}
      <header className="header-container" style={{ position: 'relative', zIndex: 10 }}>
        <Link to="/" className="btn btn-outline glass-btn header-back-minimal" style={{ textDecoration: 'none' }}>
          <ArrowLeft size={18} /> <span className="hide-mobile" style={{ marginLeft: 8 }}>{t('back_to_home')}</span>
        </Link>

        <div className="header-centered-logo">
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}>
            <Logo size={24} />
            <span style={{ fontWeight: 800, fontSize: 18, color: 'var(--text)', letterSpacing: -0.5 }}>Wafiyo</span>
          </Link>
        </div>

        <LanguageToggle />
      </header>

      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', position: 'relative' }}>
        <div className="bg-shapes hide-mobile">
          <div className="shape shape-1" style={{ top: '-10%', left: '-10%', width: 600, height: 600, opacity: 0.15 }}></div>
          <div className="shape shape-2" style={{ bottom: '-10%', right: '-10%', width: 800, height: 800, opacity: 0.1 }}></div>
        </div>

        <div className="glass-card" style={{ width: '100%', maxWidth: 420, position: 'relative', zIndex: 1 }}>
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--text)' }}>{t('loyalty_card_manager')}</h1>
            <p style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 4 }}>
              {t('sign_in_business')}
            </p>
          </div>

          {error && <div className="alert alert-error">{error}</div>}

          <form onSubmit={handleSubmit} noValidate>
            <div className="form-group">
              <label>{t('email')}</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required />
            </div>
            <div className="form-group">
              <label>{t('password')}</label>
              <div className="password-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex="-1"
                >
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                  )}
                </button>
              </div>
              <div style={{ textAlign: 'right', marginTop: 8 }}>
                <Link to="/forgot-password" style={{ color: '#8b949e', fontSize: 13, textDecoration: 'none' }}>
                  {t('forgot_password_link')}
                </Link>
              </div>
            </div>
            <button className="btn btn-primary btn-glow" type="submit" disabled={loading} style={{ width: '100%', justifyContent: 'center', marginTop: 8 }}>
              {loading ? t('please_wait') : t('sign_in')}
            </button>
          </form>

          <p style={{ textAlign: 'center', marginTop: 20, fontSize: 14, color: 'var(--text-muted)' }}>
            {t('dont_have_account')}
            <Link to="/register" style={{ background: 'none', border: 'none', color: '#60a5fa', fontWeight: 600, textDecoration: 'none' }}>
              {t('sign_up_link')}
            </Link>
          </p>
        </div>
      </div>

      {/* Standard Footer */}
      <footer className="landing-footer" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <Logo size={28} />
              <span className="logo-text">Wafiyo</span>
              <p className="footer-tagline">
                {t('footer_tagline')}
              </p>
            </div>
            <div className="footer-links">
              <div className="link-col">
                <h4>{t('legal')}</h4>
                <Link to="/privacy">{t('privacy_policy_title')}</Link>
                <Link to="/terms">{t('tos_title')}</Link>
                <Link to="/refund">{t('refund_policy_title')}</Link>
                <Link to="/contact">{t('contact_title')}</Link>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>© {new Date().getFullYear()} Wafiyo. {t('all_rights_reserved')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
